import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Send, Event, Schedule, DateRange } from '@mui/icons-material';
import { auth } from '../utils/auth';
import { saveNote } from '../utils/supabase';
import { generateContent } from '../utils/ai';

function ScheduleMaker() {
  const [description, setDescription] = useState('');
  const [schedule, setSchedule] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);
  const [scheduleType, setScheduleType] = useState('daily');

  // Schedule types
  const scheduleTypes = [
    { value: 'daily', label: 'Daily Schedule' },
    { value: 'weekly', label: 'Weekly Schedule' },
    { value: 'monthly', label: 'Monthly Schedule' },
    { value: 'study', label: 'Study Plan' },
    { value: 'project', label: 'Project Timeline' }
  ];

  // Get current user
  useEffect(() => {
    const initUser = async () => {
      try {
        const currentUser = await auth.getCurrentUser();
        setUser(currentUser);
      } catch (error) {
        console.error('Error loading user data:', error);
      }
    };
    initUser();
  }, []);

  const handleGenerateSchedule = async () => {
    if (!description.trim()) {
      setError('Please enter a description of the schedule you want to create');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setSchedule('');

    try {
      const promptContent = `You are an expert schedule maker. The user wants a ${scheduleType} schedule. 
      Create a well-structured, detailed, and realistic schedule based on their requirements.
      
      For a ${scheduleType === 'daily' ? 'daily schedule, include time slots from morning to evening with activities' : 
        scheduleType === 'weekly' ? 'weekly schedule, organize by days of the week with major activities for each day' :
        scheduleType === 'monthly' ? 'monthly schedule, highlight key events, deadlines, and milestones' :
        scheduleType === 'study' ? 'study plan, include subjects, topics, study hours, and breaks' :
        'project timeline, include phases, tasks, deadlines, and responsible parties'}.
      
      Format the schedule in a clean, readable format with clear time slots, activities, and any necessary notes.
      
      Make the schedule realistic, balanced, and tailored to the user's specific needs.`;

      const messages = [
              {
                role: 'system',
                content: promptContent
              },
              {
                role: 'user',
                content: description
              }
      ];

      const scheduleContent = await generateContent(messages, { max_tokens: 2000, temperature: 0.7 });
      setSchedule(scheduleContent.trim());
      setSuccess('Schedule generated successfully!');
    } catch (err) {
      const errorMessage = err.response?.data?.error?.message || err.response?.data?.error || err.message || 'Failed to generate schedule. Please try again.';
      setError(errorMessage);
      console.error('API Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSchedule = async () => {
    if (!user) {
      setError('You must be logged in to save schedules');
      return;
    }

    if (!schedule) {
      setError('No schedule to save');
      return;
    }

    try {
      const scheduleTitle = `${scheduleType.charAt(0).toUpperCase() + scheduleType.slice(1)} Schedule: ${description.substring(0, 20)}...`;
      await saveNote(user.id, schedule, scheduleTitle);
      setSuccess('Schedule saved successfully!');
    } catch (err) {
      setError('Failed to save schedule');
      console.error('Save error:', err);
    }
  };

  const getPlaceholderText = () => {
    switch (scheduleType) {
      case 'daily':
        return "Example: Create a daily schedule for a college student who has classes from 9 AM to 3 PM, needs to study for finals, work out, and has a part-time job from 6-9 PM.";
      case 'weekly':
        return "Example: Create a weekly schedule for a project manager who needs to balance team meetings, client calls, project work, and family time on weekends.";
      case 'monthly':
        return "Example: Create a monthly schedule for preparing for the MCAT exam while working part-time and volunteering at the hospital on weekends.";
      case 'study':
        return "Example: Create a study plan for preparing for the physics, chemistry, and biology finals over the next 3 weeks, focusing more on physics as it's my weakest subject.";
      case 'project':
        return "Example: Create a project timeline for developing a mobile app with the following phases: requirements gathering, design, development, testing, and deployment over 3 months.";
      default:
        return "Describe the schedule you want to create...";
    }
  };

  return (
    <Box sx={{ py: 4 }}>
      <Container maxWidth="lg">
        <Typography variant="h2" gutterBottom>
          AI Schedule Maker
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h5" gutterBottom>
                  How It Works
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={2}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <DateRange color="primary" />
                    <Typography>Choose a schedule type</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Event color="primary" />
                    <Typography>Describe your requirements</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Schedule color="primary" />
                    <Typography>Get an AI-generated schedule</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3, mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Generate Your Schedule
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel id="schedule-type-label">Schedule Type</InputLabel>
                <Select
                  labelId="schedule-type-label"
                  id="schedule-type"
                  value={scheduleType}
                  label="Schedule Type"
                  onChange={(e) => setScheduleType(e.target.value)}
                  disabled={loading}
                >
                  {scheduleTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Schedule Description"
                placeholder={getPlaceholderText()}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={loading}
                sx={{ mb: 3 }}
              />
              
              <Button
                variant="contained"
                size="large"
                startIcon={loading ? <CircularProgress size={20} /> : <Send />}
                onClick={handleGenerateSchedule}
                disabled={loading || !description.trim()}
                fullWidth
              >
                {loading ? 'Generating...' : 'Generate Schedule'}
              </Button>
            </Paper>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
                {success}
              </Alert>
            )}

            {schedule && (
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Your Schedule</Typography>
                  {user && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSaveSchedule}
                    >
                      Save Schedule
                    </Button>
                  )}
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ 
                  p: 2, 
                  borderRadius: 1, 
                  bgcolor: 'background.paper',
                  boxShadow: 'inset 0 0 5px rgba(0,0,0,0.1)',
                  border: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Typography 
                    component="div" 
                    sx={{ 
                      whiteSpace: 'pre-wrap', 
                      fontFamily: 'inherit',
                      '& h1, & h2, & h3': {
                        color: 'primary.main',
                        my: 1
                      },
                      '& ul, & ol': {
                        pl: 4,
                        mb: 2
                      },
                      '& table': {
                        width: '100%',
                        borderCollapse: 'collapse',
                        mb: 2,
                        '& th': {
                          bgcolor: 'primary.light',
                          color: 'primary.contrastText',
                          p: 1,
                          textAlign: 'left'
                        },
                        '& td': {
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          p: 1
                        }
                      },
                      '& strong, & b': {
                        color: 'text.primary',
                        fontWeight: 'bold'
                      },
                      '& hr': {
                        my: 2,
                        border: 'none',
                        height: '1px',
                        bgcolor: 'divider'
                      }
                    }}
                    dangerouslySetInnerHTML={{ 
                      __html: schedule
                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        .replace(/\*(.*?)\*/g, '<em>$1</em>')
                        .replace(/^# (.*?)$/gm, '<h1>$1</h1>')
                        .replace(/^## (.*?)$/gm, '<h2>$1</h2>')
                        .replace(/^### (.*?)$/gm, '<h3>$1</h3>')
                        .replace(/^(\d+:\d+\s*(?:AM|PM|am|pm))\s*-\s*(.*?)$/gm, '<div style="display:flex;margin-bottom:8px;"><div style="min-width:100px;font-weight:bold;color:#1976d2;">$1</div><div>$2</div></div>')
                        .replace(/^([A-Za-z]+day):\s*(.*?)$/gm, '<div style="display:flex;margin-bottom:8px;margin-top:16px;"><div style="min-width:100px;font-weight:bold;color:#1976d2;">$1</div><div>$2</div></div>')
                        .replace(/\n\n/g, '<br/><br/>')
                    }}
                  />
                </Box>
              </Paper>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default ScheduleMaker;
